import type { GridApi } from "ag-grid-community";

import {
	SPEC_BAND_FIELD,
	type SpecBandField,
	type SpecItemDraftRow,
} from "./SectionSpecForm";

const BAND_FIELDS: readonly SpecBandField[] = [
	SPEC_BAND_FIELD.LTL,
	SPEC_BAND_FIELD.NOMINAL,
	SPEC_BAND_FIELD.UTL,
];
const NAVIGATION_KEYS = new Set([
	"ArrowLeft",
	"ArrowRight",
	"ArrowUp",
	"ArrowDown",
	"Enter",
]);

type NavigationEvent = Pick<
	KeyboardEvent,
	| "key"
	| "altKey"
	| "ctrlKey"
	| "metaKey"
	| "shiftKey"
	| "isComposing"
	| "target"
	| "preventDefault"
	| "stopPropagation"
>;

export const navigateSectionSpecDimension = (
	event: NavigationEvent,
	api: GridApi<SpecItemDraftRow> | null,
	readOnly: boolean,
) => {
	if (
		readOnly ||
		!api ||
		api.isDestroyed() ||
		event.altKey ||
		event.ctrlKey ||
		event.metaKey ||
		event.shiftKey ||
		event.isComposing ||
		!NAVIGATION_KEYS.has(event.key)
	)
		return;

	const focused = api.getFocusedCell();
	if (!focused || focused.rowPinned) return;
	const bandIndex = BAND_FIELDS.findIndex(
		(field) => field === focused.column.getColId(),
	);
	const source = api.getDisplayedRowAtIndex(focused.rowIndex);
	if (bandIndex < 0 || !source || !focused.column.isCellEditable(source))
		return;

	if (
		(event.key === "ArrowLeft" || event.key === "ArrowRight") &&
		event.target instanceof HTMLInputElement
	) {
		const { selectionStart, selectionEnd, value } = event.target;
		const atBoundary =
			selectionStart !== null &&
			selectionStart === selectionEnd &&
			(event.key === "ArrowLeft"
				? selectionStart === 0
				: selectionEnd === value.length);
		if (!atBoundary) {
			// Keep grid navigation out of native caret movement and selection collapse.
			event.stopPropagation();
			return;
		}
		const nextPart =
			event.key === "ArrowRight" && event.target.dataset.anglePart === "Deg"
				? "Min"
				: event.key === "ArrowLeft" && event.target.dataset.anglePart === "Min"
					? "Deg"
					: undefined;
		const sibling = nextPart
			? event.target
					.closest("[data-spec-angle-editor]")
					?.querySelector<HTMLInputElement>(
						`input[data-angle-part="${nextPart}"]`,
					)
			: null;
		if (sibling) {
			event.preventDefault();
			event.stopPropagation();
			sibling.focus();
			sibling.select();
			return;
		}
	}

	// Only cell navigation consumes the key's default action.
	event.preventDefault();
	event.stopPropagation();
	let rowIndex = focused.rowIndex;
	let nextBand = bandIndex;
	const movesToNextRow =
		event.key === "Enter" && bandIndex === BAND_FIELDS.length - 1;
	if (event.key === "ArrowUp" || event.key === "ArrowDown" || movesToNextRow) {
		const direction = event.key === "ArrowUp" ? -1 : 1;
		nextBand = movesToNextRow ? 0 : bandIndex;
		const column = api.getColumn(BAND_FIELDS[nextBand]);
		for (
			rowIndex += direction;
			rowIndex >= 0 && rowIndex < api.getDisplayedRowCount();
			rowIndex += direction
		) {
			const row = api.getDisplayedRowAtIndex(rowIndex);
			if (row && column?.isCellEditable(row)) break;
		}
	} else {
		nextBand += event.key === "ArrowLeft" ? -1 : 1;
	}

	if (
		rowIndex < 0 ||
		rowIndex >= api.getDisplayedRowCount() ||
		nextBand < 0 ||
		nextBand >= BAND_FIELDS.length
	) {
		// Arrow boundaries retain the current edit; the final Enter still commits.
		if (event.key === "Enter") api.stopEditing();
		return;
	}

	const colKey = BAND_FIELDS[nextBand];
	api.stopEditing();
	api.ensureIndexVisible(rowIndex);
	api.ensureColumnVisible(colKey);
	api.setFocusedCell(rowIndex, colKey);
	api.startEditingCell({
		rowIndex,
		colKey,
		...(event.target instanceof HTMLInputElement &&
		event.target.dataset.anglePart
			? { key: event.key }
			: {}),
	});
};
